/**
 * Per-blog-post configuration for the inline "reminder widget" — a zip-code
 * email-capture box that pitches Premium reminders for the post's topic.
 *
 * The widget is injected by lib/content-processor.ts via H2-position-aware
 * markers, the same pattern as ARTICLE_PRODUCT_PICKS in
 * data/article-product-picks.ts. To add a post:
 *   1. Read the published Directus content for the slug.
 *   2. Pick an H2 boundary deep enough that the reader has investment.
 *   3. Add an entry below with a topic that matches the post's intent.
 *
 * Topic determines the widget headline and follow-up email tag. Keep topics
 * tightly scoped so the welcome email's content can be specific.
 */

export type ReminderTopic =
  | 'pre-emergent'
  | 'weed-feed'
  | 'weed-control'
  | 'weed-removal'
  | 'insect-control'
  | 'grass-identity'
  | 'bermuda-fertilizer'
  | 'centipede-fertilizer'
  | 'st-augustine-fertilizer'
  | 'aeration'
  | 'overseeding'
  | 'feeding'
  | 'lime'
  | 'seasonal'

export interface ReminderWidgetConfig {
  /** Topic governing widget copy + email tagging */
  topic: ReminderTopic
  /** 1-indexed H2 to insert before. Mirrors ARTICLE_PRODUCT_PICKS.insertAfterH2. */
  insertAfterH2: number
}

/**
 * Top-traffic posts. Originally the top 8 (~59% of blog traffic); expanded
 * 2026-06 to cover the rest of the weed-feed, pre-emergent, and fertilizer
 * clusters. insertAfterH2 is a 0-indexed array position into the post's H2s
 * (marker is inserted before that H2); values chosen to land just after each
 * post's timing/schedule section. Falls back to end-of-post if out of range.
 */
export const BLOG_REMINDER_WIDGETS: Record<string, ReminderWidgetConfig> = {
  'scotts-weed-and-feed-application-guide-best-products': { topic: 'weed-feed', insertAfterH2: 2 },
  'long-scotts-weed-feed-take-work': { topic: 'weed-feed', insertAfterH2: 2 },
  'best-fertilizer-for-bermuda-grass-top-picks-reviews': { topic: 'bermuda-fertilizer', insertAfterH2: 3 },
  'pre-emergent-timing-when-to-apply-guide': { topic: 'pre-emergent', insertAfterH2: 1 },
  'apply-crabgrass-preventer-zip-code-2026-spring-guide': { topic: 'pre-emergent', insertAfterH2: 1 },
  'often-you-apply-scotts-weed-feed': { topic: 'weed-feed', insertAfterH2: 2 },
  'best-fertilizer-centipede-grass-2026-guide': { topic: 'centipede-fertilizer', insertAfterH2: 3 },
  'scotts-weed-feed-actually-work-honest-review': { topic: 'weed-feed', insertAfterH2: 3 },
  // Expanded 2026-06 -- weed-feed + pre-emergent cluster
  'scotts-turf-builder-triple-action-instructions-complete-application-guide': { topic: 'weed-feed', insertAfterH2: 2 },
  'scotts-turf-builder-halts-crabgrass-preventer-instructions': { topic: 'pre-emergent', insertAfterH2: 2 },
  // Expanded 2026-06 -- bermuda fertilizer cluster
  'best-fertilizer-for-bermuda-grass-in-texas': { topic: 'bermuda-fertilizer', insertAfterH2: 4 },
  'best-fertilizer-for-bermuda-grass-in-georgia': { topic: 'bermuda-fertilizer', insertAfterH2: 4 },
  'best-fertilizer-for-bermuda-grass-in-florida': { topic: 'bermuda-fertilizer', insertAfterH2: 4 },
  'best-fertilizer-ratio-bermuda-grass-npk-guide': { topic: 'bermuda-fertilizer', insertAfterH2: 3 },
  // Expanded 2026-06 -- st-augustine + generic feeding
  'best-fertilizer-for-st-augustine-grass-2026-guide': { topic: 'st-augustine-fertilizer', insertAfterH2: 4 },
  'milorganite-application-guide-best-uses-2026': { topic: 'feeding', insertAfterH2: 2 },
  '10-10-10-fertilizer-when-how-to-apply': { topic: 'feeding', insertAfterH2: 2 },
  'best-summer-lawn-fertilizers-keep-grass-green-healthy': { topic: 'feeding', insertAfterH2: 3 },
  // Centipede weed-and-feed: centipede-fertilizer copy ("easy to over-feed") fits the
  // post's over-fertilization thesis better than the generic Scotts weed-feed copy.
  'best-weed-and-feed-for-centipede-grass-2026': { topic: 'centipede-fertilizer', insertAfterH2: 6 },
  // Expanded 2026-06 (round 2) -- highest-traffic widget-less posts from GSC (90d).
  // insertAfterH2 picked per each post's real H2 structure to land just after its
  // timing / when-to-apply / schedule section (the peak "when to act" moment).
  // weed-control cluster (post-emergents + selective herbicides; new topic)
  'spectracide-weed-stop-instructions-use-best-results': { topic: 'weed-control', insertAfterH2: 3 },
  'tenacity-herbicide-mesotrione-application-guide': { topic: 'weed-control', insertAfterH2: 3 },
  'best-weed-control-zoysia-grass': { topic: 'weed-control', insertAfterH2: 3 },
  'best-weed-control-bermuda-grass-safe-products-wont-kill': { topic: 'weed-control', insertAfterH2: 5 },
  // weed-feed cluster
  'scotts-weed-feed-pre-emergent': { topic: 'weed-feed', insertAfterH2: 3 },
  'how-long-does-weed-and-feed-take-to-work-timeline': { topic: 'weed-feed', insertAfterH2: 4 },
  'scotts-weed-and-feed-buyers-guide-which-one-to-buy-2026': { topic: 'weed-feed', insertAfterH2: 7 },
  'best-weed-feed-bermuda-grass-2026-guide': { topic: 'weed-feed', insertAfterH2: 4 },
  // overseeding / seeding cluster
  'pennington-smart-seed-planting-guide-step-by-step-instructions': { topic: 'overseeding', insertAfterH2: 5 },
  'slit-seeder-rental-the-complete-guide': { topic: 'overseeding', insertAfterH2: 4 },
  // feeding cluster (cool-season + zoysia + Scotts Southern)
  'best-fertilizers-for-lawns': { topic: 'feeding', insertAfterH2: 3 },
  'best-fertilizer-kentucky-bluegrass-2026-guide': { topic: 'feeding', insertAfterH2: 4 },
  'best-fertilizer-for-zoysia-grass-2026-guide': { topic: 'feeding', insertAfterH2: 4 },
  'scotts-southern-turf-builder-instructions': { topic: 'feeding', insertAfterH2: 2 },
  // st-augustine + pre-emergent
  'best-fertilizer-st-augustine-grass-2026-guide': { topic: 'st-augustine-fertilizer', insertAfterH2: 5 },
  'scotts-pre-emergent-apply-application-rate-2026-schedule': { topic: 'pre-emergent', insertAfterH2: 2 },
  // Added 2026-07-13 -- 96% bounce at 23 visitors/day with no capture surface.
  // H2 #2 is the "When to Apply" section; marker lands just after it.
  'when-to-apply-lime-to-lawn': { topic: 'lime', insertAfterH2: 3 },
  // Simple Lawn Solutions liquid-fertilizer cluster (2026). 'feeding' copy fits liquid
  // feeding. insertAfterH2 offset from each spoke's product card to avoid stacking.
  'simple-lawn-solutions-16-4-8-review': { topic: 'feeding', insertAfterH2: 4 },
  'simple-lawn-solutions-review': { topic: 'feeding', insertAfterH2: 4 },
  'best-liquid-iron-for-lawns': { topic: 'feeding', insertAfterH2: 3 },
  'best-high-nitrogen-liquid-lawn-fertilizer': { topic: 'feeding', insertAfterH2: 3 },
  '15-0-15-liquid-fertilizer-phosphorus-free': { topic: 'feeding', insertAfterH2: 5 },
  'which-simple-lawn-solutions-to-buy': { topic: 'feeding', insertAfterH2: 5 },
  'liquid-lawn-fertilizer-schedule': { topic: 'feeding', insertAfterH2: 4 },
  '3-18-18-liquid-starter-fertilizer': { topic: 'feeding', insertAfterH2: 3 },
  'how-to-apply-liquid-lawn-fertilizer': { topic: 'feeding', insertAfterH2: 5 },
  // Expanded 2026-06 (round 3) -- highest-traffic widget-less posts from GSC (90d).
  // insertAfterH2 is the 0-indexed H2 to insert *before*; values land the marker
  // just after each post's timing / when-to-apply / schedule section.
  // Seasonal green-up + recovery + timing posts (generic 'seasonal' copy, zip-tuned).
  'bermuda-grass-spring-green-up-starts-speed-up': { topic: 'seasonal', insertAfterH2: 2 },
  'revive-bermuda-grass-after-summer-heat-stress': { topic: 'seasonal', insertAfterH2: 4 },
  'bermuda-grass-turn-green-region-by-region-timeline': { topic: 'seasonal', insertAfterH2: 3 },
  // Held on 'seasonal' with the rest of the pest cluster: see the note on the
  // 'insect-control' copy below. This slug alone is 215 of that topic's 357
  // impressions, and it is an application-instructions page, so the timing
  // promise restates what the reader just read.
  'bioadvanced-bayer-grub-killer-instructions-apply': { topic: 'seasonal', insertAfterH2: 2 },
  'when-is-the-best-time-to-apply-fungicide-to-lawn': { topic: 'seasonal', insertAfterH2: 2 },
  // Fertilizer + weed clusters
  'best-fertilizer-for-st-augustine-grass-in-texas': { topic: 'st-augustine-fertilizer', insertAfterH2: 4 },
  'best-fertilizer-for-tall-fescue-2026-guide': { topic: 'feeding', insertAfterH2: 7 },
  'roundup-for-lawns-application-guide-vs-regular-roundup': { topic: 'weed-control', insertAfterH2: 2 },
  'best-weed-feed-st-augustine-grass-2026': { topic: 'weed-feed', insertAfterH2: 6 },
  // Category-offer pass 2026-08-07 -- posts pulled off the generic 'seasonal'
  // fallback. Everything here sits in a mixed Directus category ('Lawn Care
  // Tips', 'Pest Control', 'Climate & Regional Tips') that also holds posts
  // which must STAY generic, so category routing can't reach them and the slug
  // has to say it. Posts in a clean category (Grass Types) or a slug series
  // (types-of-grass-in-*, how-to-get-rid-of-*) are handled by the resolver
  // instead and are deliberately absent from this map.
  //
  // insertAfterH2 is 3 throughout, matching AUTO_WIDGET_DEFAULT_H2, so these
  // posts keep the exact placement they have today. The offer is the only thing
  // that changes, which is what makes the pre/post read of validation gate A a
  // clean one-variable comparison against the recorded 0.63% seasonal baseline.
  //
  // Every slug below was checked against Directus for status: published. Three
  // otherwise-matching posts were left out for being archived
  // (diy-lawn-pest-control-safe-options-that-actually-work,
  // dealing-summer-lawn-pests-effective-strategies, perennial-ryegrass-guide).
  //
  // Lawn insects. Distinct from weeds: the reader is holding an insecticide and
  // the question is timing, not identification. Disease posts in these same
  // categories are intentionally NOT here -- they route to the photo diagnose
  // flow, which converts far better on that intent.
  'how-to-get-rid-of-chinch-bugs': { topic: 'seasonal', insertAfterH2: 3 },
  'sod-webworms-lawn-damage-control': { topic: 'seasonal', insertAfterH2: 3 },
  'how-to-control-grubs-in-your-lawn': { topic: 'seasonal', insertAfterH2: 3 },
  'lawn-pest-control-identify-common-bugs-and-get-rid-of-them-fast': { topic: 'seasonal', insertAfterH2: 3 },
  'natural-lawn-pest-control-pet-safe-and-kid-safe-approaches': { topic: 'seasonal', insertAfterH2: 3 },
  'armyworm-control-how-to-get-rid-of-armyworms-quickly': { topic: 'seasonal', insertAfterH2: 3 },
  'mole-crickets-identify-control': { topic: 'seasonal', insertAfterH2: 3 },
  // Weed removal posts whose slug doesn't carry the how-to-get-rid-of- prefix.
  'creeping-charlie-identification-removal-guide': { topic: 'weed-removal', insertAfterH2: 3 },
  'common-lawn-weeds-and-how-to-remove-them': { topic: 'weed-removal', insertAfterH2: 3 },
  'best-crabgrass-killer-top-products-diy-methods': { topic: 'weed-removal', insertAfterH2: 3 },
  // Grass identity + species care outside the 'Grass Types' category.
  'palisades-zoysia-grass-identification-care-tips': { topic: 'grass-identity', insertAfterH2: 3 },
  'meyer-zoysia-identification-care-tips': { topic: 'grass-identity', insertAfterH2: 3 },
  'mowing-height-by-grass-type-guide': { topic: 'grass-identity', insertAfterH2: 3 },
  'st-augustine-grass-shade-grow-varieties-tips': { topic: 'grass-identity', insertAfterH2: 3 },
  'best-grass-for-texas-lawns': { topic: 'grass-identity', insertAfterH2: 3 },
  'best-cool-season-grass-northern-lawns': { topic: 'grass-identity', insertAfterH2: 3 },
  'most-common-grass-types-by-state': { topic: 'grass-identity', insertAfterH2: 3 },
  'native-grass-types-for-your-region': { topic: 'grass-identity', insertAfterH2: 3 },
  'best-grass-busy-families-kids-pets': { topic: 'grass-identity', insertAfterH2: 3 },
  'grass-types-least-mowing': { topic: 'grass-identity', insertAfterH2: 3 },
  // Sits in 'Grass Types' but is a readership survey, not a care question --
  // pinned back to the generic offer so the category rule can't mismatch it.
  'state-of-the-american-lawn-2026': { topic: 'seasonal', insertAfterH2: 3 },
  // Timing-specific offers that already existed and simply weren't wired up.
  'scotts-4-step-program-annual-schedule': { topic: 'weed-feed', insertAfterH2: 5 },
  'granular-pre-emergent-herbicide-timing-application-guide': { topic: 'pre-emergent', insertAfterH2: 3 },
  'when-to-aerate-lawn-timing-guide': { topic: 'aeration', insertAfterH2: 3 },
  'how-to-aerate-your-lawn-the-right-way': { topic: 'aeration', insertAfterH2: 3 },
  'complete-spring-lawn-aeration-guide-timing-equipment-technique': { topic: 'aeration', insertAfterH2: 3 },
  'how-to-start-a-lawn-from-scratch-2025': { topic: 'overseeding', insertAfterH2: 3 },
  'hydroseeding-guide': { topic: 'overseeding', insertAfterH2: 3 },
  'grass-seed-germination-temperature-chart-by-type': { topic: 'overseeding', insertAfterH2: 3 },
  'sod-vs-seed': { topic: 'overseeding', insertAfterH2: 3 },
  'overseeding-best-practices': { topic: 'overseeding', insertAfterH2: 3 },
}

/**
 * Topic-specific copy used by the widget. Headlines name the user's intent
 * exactly; subheadings tee up the reminder pitch without overpromising.
 */
export const REMINDER_TOPIC_COPY: Record<ReminderTopic, { title: string; subtitle: string; cta: string }> = {
  // Specificity pass (July 2026 audit): the reminder widget was shown to 521
  // unique users in 48h but converted ~1% — every topic used the same generic
  // "Get seasonal reminders" CTA. Name the exact deliverable and the moment so
  // the ask reads as one concrete thing, not a newsletter. (growth-design rule 2:
  // specificity beats abstraction — obsidian/research/growth-design/design-blunders.md)
  //
  // Deliverable pass (2026-07-22): the specificity above was real but every
  // subtitle promised a FUTURE email ("we will email you when it is time"),
  // which the seasonal cron only sends when SEASONAL_EMAILS_ENABLED is set — the
  // same promise .claude/rules/tools-pages.md tells result-screen copy not to
  // make. Meanwhile the thing that DOES send on submit, immediately, went
  // unmentioned: /api/email/subscribe routes every blog_reminder_widget_* signup
  // carrying a ZIP to sendCarePlanEmail, a 12-month plan starting with the
  // current month. Subtitles now promise that. Titles and CTAs are untouched on
  // purpose: they carry the topic hook and the PostHog source buckets, so the
  // pre/post read of this change stays clean at 1.2% baseline.
  'pre-emergent': {
    title: 'Get your pre-emergent window for your ZIP',
    subtitle: 'Miss the window and crabgrass gets a whole season. Add your ZIP and we will email your 12-month lawn calendar right away, starting with what to do this month.',
    cta: 'Get my pre-emergent date',
  },
  'weed-feed': {
    title: 'Get your weed-and-feed window for your ZIP',
    subtitle: 'Weed and feed only works in the right soil-temp and rainfall window, and it shifts by region. Add your ZIP and we will email your 12-month lawn calendar right away, starting with this month.',
    cta: 'Get my weed-and-feed window',
  },
  'weed-control': {
    title: 'Get your weed-control timing for your ZIP',
    subtitle: 'Post-emergents and selective herbicides work best in narrow temperature and growth windows. Add your ZIP and we will email your 12-month lawn calendar right away, starting with this month.',
    cta: 'Get my weed-control timing',
  },
  // Category-offer pass (2026-08-07): 'seasonal' was 59% of all widget
  // impressions at 0.63%, against 1.93%-2.72% for the named topics in the same
  // component and slot. These three topics carve the three largest coherent
  // audiences out of that fallback pool. Subtitles keep the same honest promise
  // every other topic makes (the 12-month calendar that /api/email/subscribe
  // actually sends on submit); only the hook changes.
  //
  // 'weed-control' covers product-application posts ("how do I use Tenacity").
  // 'weed-removal' covers the "how to get rid of <weed>" posts, where the
  // reader's real problem is that the weed returns, so the hook is the calendar
  // rather than the product.
  'weed-removal': {
    title: 'Get your weed timing for your ZIP',
    subtitle: 'Killing a weed once is the easy half. Stopping it coming back next spring is a calendar problem. Add your ZIP and we will email your 12-month lawn calendar right away, starting with this month.',
    cta: 'Get my weed calendar',
  },
  // DEFINED BUT UNASSIGNED — no slug routes here, and no category rule reaches
  // it, so nothing renders this today. Held back on 2026-08-08 because the
  // title promises pest timing the artifact does not contain: CarePlanEmail's
  // month tables carry only "Monitor for pests" in two warm-season months, with
  // no grub window. The intent is sound (preventive grub control really is a
  // soil-temperature window) so the copy stays ready. Assign the pest slugs
  // back once the calendar actually carries a grub window, not before.
  'insect-control': {
    title: 'Get your grub and lawn pest timing for your ZIP',
    subtitle: 'Grubs, chinch bugs and armyworms each only die inside a short window, and it moves with your soil temperature. Add your ZIP and we will email your 12-month lawn calendar right away, starting with this month.',
    cta: 'Get my treatment timing',
  },
  // Identity-cluster readers want to know what they are growing, not what to
  // buy, so this offer is species care rather than a product shortlist.
  // Promise fix (2026-08-08, one day after this topic shipped): the original
  // title was "Get the 12-month care calendar for your grass type", which the
  // widget cannot deliver. It collects a ZIP and nothing else, and
  // TOPIC_GRASS_TYPE carries no grass for this topic, so /api/email/subscribe
  // sends the generic CarePlanEmail whose audience label sanitizes to "your
  // grass". Worse, it presumes the exact answer the reader came to this page
  // WITHOUT: they are on an identification article because they do not know
  // their species. Now promises the region-keyed calendar that actually sends.
  //
  // The second sentence names the email tier (verified: /api/email/subscribe
  // sets EMAIL_UNLOCK_COOKIE on EVERY context, new and returning alike, raising
  // the anonymous ceiling 1 -> 3 per mode per month). Concrete, already shipped,
  // and previously invisible on every capture surface.
  'grass-identity': {
    title: 'Get your 12-month lawn calendar for your ZIP',
    subtitle: 'Mowing height, feeding months and watering all shift with your region and the grass you are growing. Add your ZIP and we will email your calendar right away, starting with this month. Your email also lifts your free photo IDs from 1 to 3 a month.',
    cta: 'Get my lawn calendar',
  },
  'bermuda-fertilizer': {
    title: 'Get your Bermuda feeding schedule for your ZIP',
    subtitle: 'Bermuda only takes up nitrogen once your soil warms, and that date moves north to south. Add your ZIP and we will email your 12-month Bermuda calendar right away, starting with this month.',
    cta: 'Get my Bermuda feeding dates',
  },
  'centipede-fertilizer': {
    title: 'Get your centipede feeding schedule for your ZIP',
    subtitle: 'Centipede is easy to over-feed. Add your ZIP and we will email your 12-month centipede calendar right away, starting with this month, so you feed it on the light schedule it wants.',
    cta: 'Get my centipede feeding dates',
  },
  'st-augustine-fertilizer': {
    title: 'Get your St. Augustine feeding schedule for your ZIP',
    subtitle: 'St. Augustine needs warmth and the right rainfall to feed well. Add your ZIP and we will email your 12-month St. Augustine calendar right away, starting with this month.',
    cta: 'Get my St. Augustine dates',
  },
  'lime': {
    title: 'Get your fall lime window for your ZIP',
    subtitle: 'Lime needs months to work, so the fall window matters. Add your ZIP and we will email your 12-month lawn calendar right away, starting with this month, so you can line the soil test up first.',
    cta: 'Get my lime timing',
  },
  'aeration': {
    title: 'Get your aeration window for your ZIP',
    subtitle: 'Aerate in the wrong window and you waste the work. Add your ZIP and we will email your 12-month lawn calendar right away, starting with this month.',
    cta: 'Get my aeration window',
  },
  'overseeding': {
    title: 'Get your overseeding window for your ZIP',
    subtitle: 'Sow too early or too late and the seed fails. Add your ZIP and we will email your 12-month lawn calendar right away, starting with this month.',
    cta: 'Get my seeding dates',
  },
  'feeding': {
    title: 'Get your lawn feeding schedule for your ZIP',
    subtitle: 'The right time to feed depends on your grass and your local soil temps. Add your ZIP and we will email your 12-month feeding calendar right away, starting with this month.',
    cta: 'Get my feeding schedule',
  },
  // Generic catch-all for fixed slots (guides, comparisons, blog footers) where
  // no single action-topic fits.
  'seasonal': {
    title: 'Get your seasonal lawn care calendar for your ZIP',
    subtitle: 'Every task, from feeding to weed control to aeration, has a window that shifts by region. Add your ZIP and we will email your 12-month lawn calendar right away, starting with this month.',
    cta: 'Get my seasonal calendar',
  },
}

/**
 * Default H2 index for an AUTO-resolved widget (0-indexed; marker inserted
 * before that H2, falling back to end-of-post when the post has fewer headings).
 * Lands after a few sections so the reader has some investment, and clears the
 * product-pick fallback callout that sits at H2 #1-2 (avoids stacking two
 * callouts). Falls back to end-of-post on shorter articles. Hand-mapped posts
 * in BLOG_REMINDER_WIDGETS override this with their own tuned position.
 */
export const AUTO_WIDGET_DEFAULT_H2 = 3

// Grass tag (Directus `tags`) → grass-specific fertilizer topic. Only meaningful
// for feeding/fertilizer posts; see resolveReminderTopic. Keys are lowercased.
const GRASS_TAG_TOPIC: Record<string, ReminderTopic> = {
  bermuda: 'bermuda-fertilizer',
  centipede: 'centipede-fertilizer',
  'st. augustine': 'st-augustine-fertilizer',
  'st augustine': 'st-augustine-fertilizer',
  'st-augustine': 'st-augustine-fertilizer',
}

function detectGrassFertilizerTopic(tags?: string[]): ReminderTopic | null {
  if (!tags) return null
  for (const tag of tags) {
    const hit = GRASS_TAG_TOPIC[tag.trim().toLowerCase()]
    if (hit) return hit
  }
  return null
}

/**
 * Derive a reminder topic from a post's Directus category + tags when the post
 * has no explicit BLOG_REMINDER_WIDGETS entry. Returns null for categories with
 * no timing-relevant reminder (the allowlist — those posts get no widget). This
 * lets every eligible post carry a page-matched widget without hand-mapping each
 * slug. Explicit BLOG_REMINDER_WIDGETS entries always win over this (see
 * insertReminderWidgetMarkers).
 */
export function resolveReminderTopic(
  category?: string,
  tags?: string[],
): ReminderTopic | null {
  const c = (category ?? '').toLowerCase()
  if (!c) return null

  // Feeding/fertilizer family: prefer the grass-specific topic when a grass tag
  // is present (page-matched + carries the grass into the welcome email).
  if (
    c.includes('fertiliz') ||
    c.includes('feeding') ||
    c.includes('nutrient') ||
    c.includes('soil')
  ) {
    return detectGrassFertilizerTopic(tags) ?? 'feeding'
  }
  if (c.includes('pre-emergent') || c.includes('crabgrass')) return 'pre-emergent'
  if (c.includes('weed') || c.includes('herbicide')) return 'weed-control'
  if (c.includes('seed') || c.includes('overseed') || c.includes('germinat')) {
    return 'overseeding'
  }
  if (c.includes('aerat')) return 'aeration'

  // Identity cluster. These readers are working out what they are growing, so
  // the offer is species care, not a product shortlist -- routing them to a
  // buyer's guide misreads the intent. 'Grass Types' is a clean category (every
  // post in it is an identity or species-selection question), which is why this
  // is a category rule and not a slug list. Posts in it that are NOT care
  // questions are pinned back to 'seasonal' in BLOG_REMINDER_WIDGETS.
  if (c.includes('grass type')) return 'grass-identity'

  // Everything else (mowing, watering, disease, seasonal, general)
  // gets the generic seasonal-timing widget. This used to return null — those
  // posts carried only the generic inline newsletter CTA, which the July 2026
  // PostHog audit measured at 0% conversion (0/241 shown) vs ~2% for this
  // widget. Seasonal reminders are relevant to any lawn topic.
  return 'seasonal'
}

/**
 * Slug series whose intent is unambiguous from the slug but whose Directus
 * category is shared with posts that must resolve differently. Prefix rules
 * exist so the series stays covered as it grows: `types-of-grass-in-<state>` is
 * generated state by state, and `how-to-get-rid-of-<weed>` gets a new entry
 * every time a weed page ships. Both would otherwise land back on the generic
 * fallback the day they publish.
 *
 * Returns null when the slug is in a series but the category says the post is
 * something else, so the caller falls through to the normal resolver.
 */
function resolveTopicFromSlugSeries(
  slug: string,
  category?: string,
): ReminderTopic | null {
  // 'types-of-grass-in-texas' etc. Category is 'Climate & Regional Tips', which
  // also holds genuinely seasonal regional posts, so the category can't do it.
  if (slug.startsWith('types-of-grass-in-')) return 'grass-identity'

  // 'how-to-get-rid-of-nutsedge' etc. Category is usually 'Problem Solving',
  // which also holds non-weed problems (yellowing, dormancy, bare patches).
  // 'Pest Control' is excluded because insect and disease posts share the same
  // slug shape; those are mapped explicitly in BLOG_REMINDER_WIDGETS instead.
  if (
    slug.startsWith('how-to-get-rid-of-') &&
    !(category ?? '').toLowerCase().includes('pest')
  ) {
    return 'weed-removal'
  }

  return null
}

/**
 * Posts whose reader has no lawn in frame, so no lawn-timing ask is credible.
 *
 * Suppression is not neutral and it is not free to skip: `processContentForContext`
 * allows one widget per H2 section, so an ask nobody can act on spends slot budget
 * a better surface could use, plus the reader's interruption tolerance.
 *
 * Deliberately an explicit slug list, not a category or keyword rule. The category
 * is exactly what fails here (both Ortho posts are `Weed Control`), and loose
 * substring rules have over-matched on this codebase before ("rust" matching
 * "trust"). Each entry below is evidenced, 30d:
 *
 *   - ortho-groundclear-vs-roundup-which-to-use: 596 sessions, 209 widget+modal
 *     impressions, 0 captures. A non-selective vegetation killer for gravel
 *     driveways and fence lines; the post's own lede says it "is a disaster when
 *     you're aiming at dandelions in the front yard". Its sibling
 *     ortho-weedclear (a lawn product, same category, same topic, same slot
 *     index) converts 2.4%. At WeedClear's per-impression rate this post would
 *     have produced ~3.5 captures. Small n, so the magnitude is directional, but
 *     the intent mismatch is structural: every surface here assumes a lawn.
 *   - 5-differences-between-ego-vs-greenworks-mower: 473 sessions, 254
 *     impressions, 0 captures. Gear purchase intent, no timing question at all.
 *
 * NOT suppressed, despite also sitting at 0 captures:
 * revive-bermuda-grass-after-summer-heat-stress (473 sessions). That reader has a
 * lawn and a timing problem; its 0.26 impressions-per-session says the ask is
 * placed too deep to be seen, which is a REACH failure, not an intent failure.
 * Suppressing it would delete an ask that has never really been tried. The reach
 * fix is a slot-priority decision (see the note above insertCoordinatedBodyMarkers
 * in lib/content-processor.ts), not more suppression.
 *
 * Wins over BLOG_REMINDER_WIDGETS and every resolver rule, so a suppressed slug
 * stays suppressed even if someone later adds it to the explicit map.
 */
export const NO_REMINDER_ASK_SLUGS: ReadonlySet<string> = new Set([
  'ortho-groundclear-vs-roundup-which-to-use',
  '5-differences-between-ego-vs-greenworks-mower',
])

/**
 * Topics that classify correctly but must not render an email reminder ask.
 *
 * 'grass-identity' (suppressed 2026-09-13): 2,613 impressions produced 10 captures
 * over 28 days, 0.38%, the worst of the five live topics against weed-feed's 3.22%.
 * The intent is not a timing question, so a future-dated email is the wrong
 * deliverable, and these readers already get two better-matched scan routes on the
 * same page: the inline scan CTA from `lib/blog-contextual-tool.ts`
 * (`isGrassIdentityIntent` -> /#analysis-card) and the grass-ID app-download card.
 * Freeing the slot returns reading and ad space rather than adding a third CTA,
 * which also keeps the `blog_identify_cta_v2` treatment uncontested.
 *
 * Suppression is applied to the RESOLVED topic, not to the classification rules, so
 * the category rule, the `types-of-grass-in-<state>` series rule, and the explicit
 * slug entries all stay intact for `scripts/audit-widget-placement.ts` and any
 * future non-email surface that wants the same routing.
 */
const NO_REMINDER_ASK_TOPICS: ReadonlySet<ReminderTopic> = new Set<ReminderTopic>([
  'grass-identity',
])

/**
 * Resolve the reminder topic for a specific post. Suppression wins, then explicit
 * slug mappings, then the slug-series rules, then the category/tag resolver.
 * A resolved topic in NO_REMINDER_ASK_TOPICS is dropped last, so no rule can
 * route a post back into a suppressed ask.
 */
export function resolveWidgetTopicForPost(
  slug?: string,
  category?: string,
  tags?: string[],
): ReminderTopic | null {
  const topic = resolveWidgetTopicUnfiltered(slug, category, tags)
  if (topic && NO_REMINDER_ASK_TOPICS.has(topic)) return null
  return topic
}

function resolveWidgetTopicUnfiltered(
  slug?: string,
  category?: string,
  tags?: string[],
): ReminderTopic | null {
  if (slug) {
    if (NO_REMINDER_ASK_SLUGS.has(slug.toLowerCase())) return null

    const config = BLOG_REMINDER_WIDGETS[slug]
    if (config) return config.topic

    const seriesTopic = resolveTopicFromSlugSeries(slug, category)
    if (seriesTopic) return seriesTopic
  }

  return resolveReminderTopic(category, tags)
}
